<template>
  <div>
    <NavigationBar titel="Dienstbericht erstellen" showBackBtn></NavigationBar>
    <Loading v-if="helpers.loading" overlay></Loading>
    <v-container v-else-if="!dutyTypes.length > 0">
      <v-alert
        type="error"
        border="left"
        colored-border
        elevation="2"
        class="mt-6"
      >
        <div class="title">Keine Dienstarten verfügbar</div>
        <div class="mt-2">
          Es sind keine Dienstarten für die Berichtserstellung verfügbar. Bitte
          deinen Administrator, Dienstarten anzulegen und zu konfigurieren.
        </div>
      </v-alert>
    </v-container>
    <template v-else>
      <v-container>
        <v-row justify="center" align="center">
          <v-col cols="12">
            <template>
              <v-stepper v-model="step" vertical>
                <!-- Step 1: Event-based or not -->
                <v-stepper-step :complete="step > 1" step="1">
                  Bericht basierend auf Veranstaltung erstellen?
                </v-stepper-step>

                <v-stepper-content step="1">
                  <v-card outlined>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-card
                            outlined
                            @click="selectEventBased(true)"
                            :style="
                              isEventBased === true
                                ? 'border: 3px solid #1867c0 !important'
                                : ''
                            "
                            class="pa-4 text-center"
                            style="cursor: pointer"
                          >
                            <v-icon large color="primary" class="mb-2"
                              >mdi-calendar-check</v-icon
                            >
                            <div class="subtitle-1 font-weight-medium">
                              Ja, basierend auf Veranstaltung
                            </div>
                            <div class="caption grey--text">
                              Daten aus Veranstaltung übernehmen
                            </div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-card
                            outlined
                            @click="selectEventBased(false)"
                            :style="
                              isEventBased === false
                                ? 'border: 3px solid #1867c0 !important'
                                : ''
                            "
                            class="pa-4 text-center"
                            style="cursor: pointer"
                          >
                            <v-icon large color="primary" class="mb-2"
                              >mdi-file-document-edit-outline</v-icon
                            >
                            <div class="subtitle-1 font-weight-medium">
                              Nein, ohne Veranstaltung
                            </div>
                            <div class="caption grey--text">
                              Leeren Dienstbericht erstellen
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        depressed
                        color="primary"
                        :disabled="isEventBased === null"
                        @click="proceedAfterEventBasedChoice()"
                      >
                        Weiter
                        <v-icon right>mdi-chevron-right</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

                <!-- Step 2: Event selection (if event-based) or Duty type selection (if not) -->
                <v-stepper-step :complete="step > 2" step="2">
                  {{
                    isEventBased
                      ? "Veranstaltung auswählen"
                      : "Dienstart auswählen"
                  }}
                </v-stepper-step>

                <v-stepper-content step="2">
                  <!-- Event selection -->
                  <v-card v-if="isEventBased" outlined>
                    <v-data-table
                      :items="last5AndOngoingEvents"
                      :headers="headers"
                      item-key="meta.id"
                      item-class="selectionStyle"
                      :sort-desc="false"
                      disable-pagination
                      hide-default-footer
                      mobile-breakpoint="0"
                      fixed-header
                      height="35vh"
                      class="mt-1"
                      no-data-text="Keine Veranstaltungen vorhanden"
                    >
                      <template v-slot:[`item.type.title`]="{ item }">
                        <event-type-chip :eventType="item.type" small>
                        </event-type-chip>
                      </template>
                      <template v-slot:[`item.eventSelection`]="{ item }">
                        <v-btn
                          v-if="item.connectedDutyReportId"
                          small
                          depressed
                          color="warning"
                          :to="{
                            name: 'duty-management-reports-details',
                            params: {
                              organizationId: $route.params.organizationId,
                              itemId: item.connectedDutyReportId,
                            },
                          }"
                          ><v-icon left small>mdi-open-in-app</v-icon
                          >Öffnen</v-btn
                        >
                        <v-btn
                          v-else
                          small
                          depressed
                          color="primary"
                          @click="selectEvent(item)"
                        >
                          Auswählen</v-btn
                        >
                      </template>
                      <template
                        v-slot:[`item.duration.start.firebaseFormat`]="{ item }"
                      >
                        {{ item.duration.start.timestamp }}
                      </template>
                    </v-data-table>

                    <v-divider></v-divider>

                    <v-card-actions>
                      <v-btn
                        depressed
                        :disabled="helpers.reportCreationDialog.loading"
                        @click="step = 1"
                      >
                        <v-icon left>mdi-chevron-left</v-icon>Zurück
                      </v-btn>
                      <v-spacer></v-spacer>
                    </v-card-actions>
                  </v-card>

                  <!-- Duty type selection -->
                  <v-card v-else outlined>
                    <v-card-text>
                      <v-item-group
                        v-if="dutyTypes.length"
                        v-model="helpers.reportCreationDialog.dutyTypeId"
                        mandatory
                      >
                        <v-row class="match-height">
                          <v-col
                            v-for="dutyType in dutyTypes"
                            :key="dutyType.meta.id"
                            cols="12"
                            md="6"
                          >
                            <v-item
                              v-slot="{ active, toggle }"
                              :value="dutyType.meta.id"
                            >
                              <v-card
                                outlined
                                @click="toggle"
                                :style="
                                  active
                                    ? `border: 3px solid #1867c0 !important`
                                    : ''
                                "
                              >
                                <v-list-item>
                                  <v-list-item-icon>
                                    <data-avatar
                                      :icon="dutyType.icon"
                                      :color="dutyType.color"
                                    ></data-avatar>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <v-list-item-title
                                      class="font-weight-medium"
                                    >
                                      {{ dutyType.title }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                      {{
                                        dutyType.description
                                      }}</v-list-item-subtitle
                                    >
                                  </v-list-item-content>
                                </v-list-item>
                              </v-card>
                            </v-item>
                          </v-col>
                        </v-row>
                      </v-item-group>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn depressed @click="step = 1">
                        <v-icon left>mdi-chevron-left</v-icon>Zurück
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn depressed color="primary" @click="step = 3">
                        Weiter
                        <v-icon right>mdi-chevron-right</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

                <!-- Step 3: Confirmation & start -->
                <v-stepper-step :complete="step > 3" step="3">
                  Dienstmodus starten
                </v-stepper-step>

                <v-stepper-content step="3">
                  <v-card flat>
                    <v-card-text class="pt-0">
                      <v-row class="match-height" dense>
                        <v-col
                          v-if="
                            helpers.reportCreationDialog.dutyTypeId &&
                            currentDutyType
                          "
                          cols="12"
                          md="6"
                        >
                          <div>Ausgewählte Dienstart:</div>
                        </v-col>
                        <v-col
                          v-if="
                            helpers.reportCreationDialog.eventId && currentEvent
                          "
                          cols="12"
                          md="6"
                        >
                          <div>Ausgewählte Veranstaltung:</div>
                        </v-col>
                      </v-row>
                      <v-row class="match-height" dense>
                        <v-col
                          v-if="
                            helpers.reportCreationDialog.dutyTypeId &&
                            currentDutyType
                          "
                          cols="12"
                          md="6"
                        >
                          <v-card outlined>
                            <v-list-item>
                              <v-list-item-icon>
                                <data-avatar
                                  :icon="currentDutyType.icon"
                                  :color="currentDutyType.color"
                                ></data-avatar>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-medium">
                                  {{ currentDutyType.title }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                  {{
                                    currentDutyType.description
                                  }}</v-list-item-subtitle
                                >
                              </v-list-item-content>
                            </v-list-item>
                          </v-card>
                        </v-col>

                        <v-col
                          v-if="
                            helpers.reportCreationDialog.eventId && currentEvent
                          "
                          cols="12"
                          md="6"
                        >
                          <v-card outlined>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title
                                  class="subtitle-1 font-weight-medium"
                                >
                                  <span>{{ currentEvent.title }}</span>
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                  <p style="line-height: 1.6" class="mb-0">
                                    <v-icon small left
                                      >mdi-calendar-badge</v-icon
                                    >{{ currentEvent.type.title }}
                                    <br />
                                    <v-icon small left
                                      >mdi-calendar-clock</v-icon
                                    >{{ currentEvent.duration.start.timestamp }}
                                    bis
                                    {{ currentEvent.duration.end.timestamp }}
                                  </p>
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-card>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-alert text color="primary" type="info">
                            <div class="title">Hinweis</div>
                            <div class="mt-2">
                              Gestartete Dienste können in Echtzeit von anderen
                              Personen eingesehen und bearbeitet werden.
                            </div>
                          </v-alert>
                          <v-alert
                            v-if="
                              this.helpers.reportCreationDialog.errorMessage
                            "
                            text
                            color="error"
                            type="error"
                          >
                            <div class="title">Fehler</div>
                            <div class="mt-2">
                              {{
                                this.helpers.reportCreationDialog.errorMessage
                              }}
                            </div>
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn
                        v-if="!helpers.reportCreationDialog.isPrefilled"
                        depressed
                        @click="step = 2"
                        :disabled="helpers.reportCreationDialog.loading"
                      >
                        <v-icon left>mdi-chevron-left</v-icon>Zurück
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                        depressed
                        color="primary"
                        :loading="helpers.reportCreationDialog.loading"
                        @click="createReport()"
                      >
                        <v-icon left>mdi-ray-start-arrow</v-icon>
                        Im Dienstmodus starten
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>
              </v-stepper>
            </template>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </div>
</template>

<script>
import { db, functions, Timestamp } from "@/firebase";
import { DUTY_MANAGEMENT } from "@/store/modules.js";
import { GET_REPORT_TYPES } from "@/store/action-types.js";
import Loading from "@/components/_system/helpers/Loading.vue";
import DataAvatar from "@/components/_systemwide/DataAvatar.vue";
import EventTypeChip from "@/components/scheduling/EventTypeChip.vue";
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";

export default {
  name: "duty-management-reports-new",
  components: {
    Loading,
    DataAvatar,
    EventTypeChip,
    NavigationBar,
    // Subheader,
  },
  data() {
    return {
      step: 1,
      isEventBased: null,
      stepperState: 1,
      loading: true,
      headers: [
        { text: "Veranstaltungstitel", value: "title" },
        { text: "Veranstaltungsart", value: "type.title" },
        { text: "Startzeitpunkt", value: "duration.start.firebaseFormat" },
        {
          text: "Endzeitpunkt",
          value: "duration.end.timestamp",
          sortable: false,
        },
        // { text: "Status", value: "status" },
        { text: "", value: "eventSelection", sortable: false },
      ],
      actions: [
        {
          title: "Speichern",
          icon: "mdi-content-save",
          actionStyle: "textBtnSubmit",
          function: this.createItem,
        },
      ],

      rules: {
        required: (value) => !!value || "Feld ist erforderlich.",
      },
      report: {
        title: "",
        connectedEventId: "",
        duration: {
          start: { timestamp: null, weekday: null },
          end: { timestamp: null, isOpen: false, weekday: null },
          inHours: 0,
          inMinutes: 0,
        },
        dutyType: {
          id: "",
          title: "",
        },
        incident: {
          code: "",
          number: "",
          date: null,
        },
        payrollType: {
          id: "",
          title: "",
          formula: "",
        },
        status: "in progress", // "in progress", "confirmed", "locked"
        isLocked: false,
        units: {
          ids: [],
          data: [],
        },
        attendances: {
          data: [],
          personIds: [],
          presentPersonIds: [],
          absentPersonIds: [],
          excusedPersonIds: [],
          unexcusedPersonIds: [],
        },
        logs: [],
      },
      eventData: null,
      helpers: {
        loading: true,
        missingDutyTypeError: false, // TODO: change behaviour to show a dialog
        formIsValid: false,
        reportCreationDialog: {
          model: false,
          loading: false,
          errorMessage: "",
          dutyTypeId: "",
          eventId: "",
          isPrefilled: false,
        },
      },
      last5AndOngoingEvents: [],

      eventsUnsubscribe: null,
    };
  },
  beforeDestroy() {
    if (this.eventsUnsubscribe) {
      this.eventsUnsubscribe();
      this.eventsUnsubscribe = null;
    }
  },
  computed: {
    dutyTypes() {
      // sort these by sortKey ascending
      const dutyType = this.$store.state.dutyManagement.dutyTypes;
      return dutyType.sort((a, b) => a.sortKey - b.sortKey);
    },
    currentDutyType() {
      return this.dutyTypes.find(
        (dutyType) =>
          dutyType.meta.id === this.helpers.reportCreationDialog.dutyTypeId,
      );
    },
    currentEvent() {
      if (
        this.eventData &&
        this.eventData.meta &&
        this.eventData.meta.id === this.helpers.reportCreationDialog.eventId
      ) {
        return this.eventData;
      } else {
        return this.last5AndOngoingEvents.find(
          (event) =>
            event.meta.id === this.helpers.reportCreationDialog.eventId,
        );
      }
    },
  },
  created() {
    this.initialize();
  },
  watch: {
    $route: "initialize",
  },
  methods: {
    async initialize() {
      this.$store.dispatch(`${DUTY_MANAGEMENT}${GET_REPORT_TYPES}`, {
        organizationId: this.$route.params.organizationId,
        query: {
          onlyActive: true,
        },
      });

      this.getEventsForSelection();

      const { dutyTypeId, eventId } = this.$route.query;
      if (dutyTypeId) {
        this.helpers.reportCreationDialog.dutyTypeId = dutyTypeId;
      }
      if (eventId) {
        this.helpers.reportCreationDialog.eventId = eventId;
        this.isEventBased = true;
        this.eventData = await this.getEvent(eventId);
        // Auto-set dutyTypeId from event if available
        if (
          this.eventData &&
          this.eventData.type &&
          this.eventData.type.dutyTypeId
        ) {
          this.helpers.reportCreationDialog.dutyTypeId =
            this.eventData.type.dutyTypeId;
        }
      }

      if (dutyTypeId && eventId) {
        this.helpers.reportCreationDialog.isPrefilled = true;
        this.step = 3;
      } else if (eventId) {
        // Event provided but no dutyTypeId: skip to confirmation since dutyType is derived from event
        this.helpers.reportCreationDialog.isPrefilled = true;
        this.step = 3;
      } else if (dutyTypeId) {
        // Only dutyTypeId provided: skip event choice, go to duty type selection pre-filled
        this.isEventBased = false;
        this.helpers.reportCreationDialog.isPrefilled = true;
        this.step = 3;
      }
      this.helpers.loading = false;
    },
    selectEventBased(value) {
      this.isEventBased = value;
    },
    proceedAfterEventBasedChoice() {
      if (this.isEventBased === null) return;
      // Reset selections when changing path
      this.helpers.reportCreationDialog.eventId = "";
      if (this.isEventBased) {
        // Going to event selection, clear manual duty type
        this.helpers.reportCreationDialog.dutyTypeId = "";
      }
      this.step = 2;
    },
    selectEvent(event) {
      this.helpers.reportCreationDialog.eventId = event.meta.id;
      // Auto-set dutyTypeId from the selected event's type
      if (event.type && event.type.dutyTypeId) {
        this.helpers.reportCreationDialog.dutyTypeId = event.type.dutyTypeId;
      }
      this.step = 3;
    },
    getEventsForSelection() {
      // Clear previous listener if exists
      if (this.eventsUnsubscribe) {
        this.eventsUnsubscribe();
        this.eventsUnsubscribe = null;
      }

      var options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };

      this.eventsUnsubscribe = db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("events")
        .where(
          "duration.start.timestamp",
          "<=",
          Timestamp.fromDate(
            new Date(new Date().getTime() + 4 * 60 * 60 * 1000),
          ),
        )
        .orderBy("duration.start.timestamp", "desc")
        .limit(10)
        .onSnapshot(
          (querySnapshot) => {
            const events = [];
            querySnapshot.forEach((doc) => {
              events.push({
                ...doc.data(),
                // overwrite
                duration: {
                  start: {
                    firebaseFormat: doc.data().duration.start.timestamp,
                    dateFormat: doc.data().duration.start.timestamp.toDate(),
                    timestamp: doc
                      .data()
                      .duration.start.timestamp.toDate()
                      .toLocaleString("de-DE", options),
                    isOpen: doc.data().duration.start.isOpen,
                  },
                  end: {
                    firebaseFormat: doc.data().duration.end.timestamp,
                    timestamp: doc
                      .data()
                      .duration.end.timestamp.toDate()
                      .toLocaleString("de-DE", options),
                    isOpen: doc.data().duration.end.isOpen,
                  },
                },
              });
            });
            this.last5AndOngoingEvents = events;
          },
          (error) => console.log(error),
        );
    },
    btnAction() {
      if (this.stepperState === 1) {
        this.stepperState = 2;
      } else {
        alert("done");
      }
    },
    async getEvent(eventId) {
      var options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("events")
        .doc(eventId)
        .get()
        .then((doc) => {
          if (doc.exists) {
            return {
              ...doc.data(),
              // overwrite
              duration: {
                start: {
                  firebaseFormat: doc.data().duration.start.timestamp,
                  dateFormat: doc.data().duration.start.timestamp.toDate(),
                  timestamp: doc
                    .data()
                    .duration.start.timestamp.toDate()
                    .toLocaleString("de-DE", options),
                  isOpen: doc.data().duration.start.isOpen,
                },
                end: {
                  firebaseFormat: doc.data().duration.end.timestamp,
                  timestamp: doc
                    .data()
                    .duration.end.timestamp.toDate()
                    .toLocaleString("de-DE", options),
                  isOpen: doc.data().duration.end.isOpen,
                },
              },
            };
          }
          return null;
        })
        .catch(function (error) {
          console.log("Error getting document:", error);
          return null;
        });
    },
    createReport() {
      try {
        if (this.helpers.reportCreationDialog.dutyTypeId) {
          this.helpers.reportCreationDialog.loading = true;
          this.helpers.reportCreationDialog.errorMessage = "";

          const callFunction = functions.httpsCallable(
            "dutyManagement-createReport",
          );
          return callFunction({
            organizationId: this.$route.params.organizationId,
            dutyTypeId: this.helpers.reportCreationDialog.dutyTypeId,
            eventId: this.helpers.reportCreationDialog.eventId,
          })
            .then((response) => {
              const reportId = response.data;
              this.$router.push({
                name: "duty-management-reports-live",
                params: {
                  organizationId: this.$route.params.organizationId,
                  itemId: reportId,
                },
              });
            })
            .catch((error) => {
              this.helpers.reportCreationDialog.errorMessage = error.message;
              this.helpers.reportCreationDialog.loading = false;
              console.error("Error creating report: ", error);
            });
        } else {
          alert("Prüfen Deine Eingabe und versuchen es erneut.");
        }
      } catch (error) {
        this.helpers.reportCreationDialog.errorMessage =
          "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.";
        this.helpers.reportCreationDialog.loading = false;
        console.error("Unexpected error: ", error);
      }
    },
  },
};
</script>

<style></style>
